import React, { useMemo, useState } from "react";
import BauhausService from "../services/bauhaus-service";
import {
  EuiButtonIcon,
  EuiToolTip,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiHealth,
  EuiSpacer,
  EuiCallOut,
  EuiLoadingSpinner,
  EuiPanel,
  EuiTitle,
  EuiHorizontalRule,
  EuiCodeBlock,
  EuiIcon,
  EuiBasicTable,
  EuiBadge,
  EuiStat,
  EuiTabs,
  EuiTab,
  EuiCopy,
  EuiButtonEmpty,
} from "@elastic/eui";

/** Keys outbound */
const BAUHAUS_KEYS = [
  "conoceOfertaEnviada",
  "sabeEstadoCliente",
  "objetivoClaroLlamada",

  "presentacionContextualizacion",
  "marcaMotivoClaridad",
  "controlInicioConversacion",

  "preguntaEstadoDecision",
  "identificaObjecionesConcretas",
  "confirmaNecesidadesReales",

  "defiendeValorSolucion",
  "ajustaDiscursoSegunObjecion",
  "sabeCuandoNoNegociarPrecio",
  "proponeAlternativasSinDevaluar",

  "detectaOportunidadMejora",
  "presentaExtraComoValor",
  "noSaturaVentaPrincipal",

  "intentaCierreClaro",
  "obtieneInformacionAccionable",
  "validaMotivosDecision",
  "dejaSiguientePasoClaro",

  "lenguajeComprensible",
  "vocabularioPositivoVariado",
  "claridadBrevedadMensajes",
  "velocidadExposicion",
  "seguridadArgumentacion",

  "escuchaActiva",
  "amabilidadCortesia",
  "formaDirigirseCliente",
  "empatia",

  "validacionTiemposNecesidad",
  "actualizacionProyecto",
  "gestionSuministroPlazos",

  "garantiaServicioTresAnos",
  "propuestaFinanciacionOferta",
];

/** Pesos outbound (fracción 0..1) */
const WEIGHTS = {
  conoceOfertaEnviada: 0.015,
  sabeEstadoCliente: 0.015,
  objetivoClaroLlamada: 0.015,

  presentacionContextualizacion: 0.0225,
  marcaMotivoClaridad: 0.0225,
  controlInicioConversacion: 0.0225,

  preguntaEstadoDecision: 0.03,
  identificaObjecionesConcretas: 0.03,
  confirmaNecesidadesReales: 0.03,

  defiendeValorSolucion: 0.0225,
  ajustaDiscursoSegunObjecion: 0.0225,
  sabeCuandoNoNegociarPrecio: 0.0225,
  proponeAlternativasSinDevaluar: 0.0225,

  detectaOportunidadMejora: 0.0225,
  presentaExtraComoValor: 0.0225,
  noSaturaVentaPrincipal: 0.0225,

  intentaCierreClaro: 0.0225,
  obtieneInformacionAccionable: 0.0225,
  validaMotivosDecision: 0.0225,
  dejaSiguientePasoClaro: 0.0225,

  lenguajeComprensible: 0.024,
  vocabularioPositivoVariado: 0.024,
  claridadBrevedadMensajes: 0.024,
  velocidadExposicion: 0.024,
  seguridadArgumentacion: 0.024,

  escuchaActiva: 0.045,
  amabilidadCortesia: 0.045,
  formaDirigirseCliente: 0.045,
  empatia: 0.045,

  validacionTiemposNecesidad: 0.0417,
  actualizacionProyecto: 0.0417,
  gestionSuministroPlazos: 0.0417,

  garantiaServicioTresAnos: 0.0625,
  propuestaFinanciacionOferta: 0.0625,
};

const CRITICAL_KEYS = new Set(["escuchaActiva"]);

/** Etiquetas UI outbound */
const LABELS = {
  valoracionAgente: "Valoración del agente",
  sentimientoCliente: "Sentimiento del cliente",
  etiquetaLlamada: "Etiqueta de la llamada",
  score100: "Score 008Q (0-100)",

  conoceOfertaEnviada: "1.1 Conoce la oferta enviada",
  sabeEstadoCliente: "1.2 Sabe el estado del cliente",
  objetivoClaroLlamada: "1.3 Objetivo claro de la llamada",

  presentacionContextualizacion: "2.1 Se presenta y contextualiza la llamada",
  marcaMotivoClaridad: "2.2 Marca el motivo con claridad",
  controlInicioConversacion: "2.3 Controla el inicio de la conversación",

  preguntaEstadoDecision: "3.1 Pregunta por el estado real de decisión",
  identificaObjecionesConcretas: "3.2 Identifica objeciones concretas",
  confirmaNecesidadesReales: "3.4 Confirma necesidades reales",

  defiendeValorSolucion: "4.1 Defiende el valor de la solución",
  ajustaDiscursoSegunObjecion: "4.2 Ajusta el discurso según objeción",
  sabeCuandoNoNegociarPrecio: "4.3 Sabe cuándo no negociar precio",
  proponeAlternativasSinDevaluar: "4.4 Propone alternativas sin devaluar la oferta",

  detectaOportunidadMejora: "5.1 Detecta oportunidad real de mejora/ampliación",
  presentaExtraComoValor: "5.2 Presenta el extra como valor",
  noSaturaVentaPrincipal: "5.3 No satura ni rompe la venta principal",

  intentaCierreClaro: "6.1 Intenta cierre claro",
  obtieneInformacionAccionable: "6.2 Obtiene información accionable",
  validaMotivosDecision: "6.3 Valida los motivos de la decisión",
  dejaSiguientePasoClaro: "6.4 Deja siguiente paso claro",

  lenguajeComprensible: "7.1 Uso de lenguaje comprensible",
  vocabularioPositivoVariado: "7.2 Vocabulario positivo y variado",
  claridadBrevedadMensajes: "7.3 Claridad y brevedad en los mensajes",
  velocidadExposicion: "7.4 Velocidad de exposición",
  seguridadArgumentacion: "7.5 Seguridad en la argumentación",

  escuchaActiva: "8.1 Escucha activa (Crítico)",
  amabilidadCortesia: "8.2 Amabilidad y cortesía",
  formaDirigirseCliente: "8.3 Forma de dirigirse al cliente",
  empatia: "8.4 Empatía",

  validacionTiemposNecesidad: "9.1 Validación de tiempos y necesidad",
  actualizacionProyecto: "9.2 Actualización del proyecto",
  gestionSuministroPlazos: "9.3 Gestión de suministro y plazos",

  garantiaServicioTresAnos: "10.1 Garantía del servicio de 3 años",
  propuestaFinanciacionOferta: "10.2 Propuesta de financiación/oferta",

  summarization: "Resumen de la llamada",
  topics: "Temas",
  agent_email: "Agente (email)",
  agent: "Agente",
  queue: "Queue",
  extension: "Extensión",
  src: "Origen",
  dst: "Destino",
  direction: "Dirección",
  answered_time: "Tiempo hablado",
  dialed_time: "Tiempo total",
  uuid: "UUID",
};

const SECTIONS = [
  {
    title: "KPIs",
    percent: null,
    keys: [
      "valoracionAgente",
      "sentimientoCliente",
      "etiquetaLlamada",
      "agent",
      "agent_email",
      "queue",
      "extension",
      "src",
      "dst",
      "direction",
      "answered_time",
      "dialed_time",
    ],
  },
  {
    title: "1. Preparación y enfoque de la llamada",
    percent: "4.5%",
    keys: ["conoceOfertaEnviada", "sabeEstadoCliente", "objetivoClaroLlamada"],
  },
  {
    title: "2. Apertura y control de la conversación",
    percent: "6.75%",
    keys: ["presentacionContextualizacion", "marcaMotivoClaridad", "controlInicioConversacion"],
  },
  {
    title: "3. Diagnóstico real",
    percent: "9%",
    keys: ["preguntaEstadoDecision", "identificaObjecionesConcretas", "confirmaNecesidadesReales"],
  },
  {
    title: "4. Capacidad de negociación y propuesta de valor",
    percent: "9%",
    keys: [
      "defiendeValorSolucion",
      "ajustaDiscursoSegunObjecion",
      "sabeCuandoNoNegociarPrecio",
      "proponeAlternativasSinDevaluar",
    ],
  },
  {
    title: "5. Upselling y crosselling",
    percent: "6.75%",
    keys: ["detectaOportunidadMejora", "presentaExtraComoValor", "noSaturaVentaPrincipal"],
  },
  {
    title: "6. Cierre o extracción de información útil",
    percent: "9%",
    keys: ["intentaCierreClaro", "obtieneInformacionAccionable", "validaMotivosDecision", "dejaSiguientePasoClaro"],
  },
  {
    title: "7. Recursos comunicativos",
    percent: "12%",
    keys: [
      "lenguajeComprensible",
      "vocabularioPositivoVariado",
      "claridadBrevedadMensajes",
      "velocidadExposicion",
      "seguridadArgumentacion",
    ],
  },
  {
    title: "8. Trato al cliente",
    percent: "18%",
    keys: ["escuchaActiva", "amabilidadCortesia", "formaDirigirseCliente", "empatia"],
  },
  {
    title: "9. Producto suministro plazo",
    percent: "12.51%",
    keys: ["validacionTiemposNecesidad", "actualizacionProyecto", "gestionSuministroPlazos"],
  },
  {
    title: "10. Beneficios",
    percent: "12.5%",
    keys: ["garantiaServicioTresAnos", "propuestaFinanciacionOferta"],
  },
];

const STATUS_COLOR = (val) => {
  switch (String(val || "").toUpperCase()) {
    case "SI_CUMPLE":
      return "success";
    case "NO_CUMPLE":
      return "danger";
    case "NO_APLICA":
      return "warning";
    default:
      return "subdued";
  }
};

function formatConfidence(x) {
  if (x === undefined || x === null || x === "") return null;
  const n = Number(x);
  if (Number.isNaN(n)) return null;
  if (n >= 0 && n <= 1) return `${Math.round(n * 100)}%`;
  if (n > 1 && n <= 100) return `${Math.round(n)}%`;
  return `${n}`;
}

function formatWeight(key) {
  const w = WEIGHTS[key];
  if (!w) return null;
  return `${(w * 100).toFixed(2)}%`;
}

function getEvalValue(payload, key) {
  if (!payload) return undefined;
  if (payload[key] != null) return payload[key];
  return payload?.estandarBauhausResultado?.[key];
}

function getDetail(payload, key) {
  const flat = payload?.estandarBauhausDetalle;
  const confFromFlat = flat?.[`${key}.confianza`];
  const justFromFlat = flat?.[`${key}.justificacion`];

  const confFromObj = payload?.estandarBauhausConfianza?.[key];
  const justFromObj = payload?.estandarBauhausJustificacion?.[key];

  const conf = confFromFlat ?? confFromObj;
  const just = justFromFlat ?? justFromObj;

  return { conf, just };
}

function computeScore100Fallback(payload) {
  let num = 0;
  let den = 0;

  const failedCritical = Array.from(CRITICAL_KEYS).some((k) => {
    const v = String(getEvalValue(payload, k) || "").toUpperCase();
    return v === "NO_CUMPLE";
  });

  if (failedCritical) return 0;

  for (const k of BAUHAUS_KEYS) {
    const w = WEIGHTS[k] ?? 0;
    const v = String(getEvalValue(payload, k) || "").toUpperCase();

    if (v === "NO_APLICA" || v === "" || v === "NO_DETECTADO") continue;

    if (v === "SI_CUMPLE" || v === "NO_CUMPLE") {
      den += w;
      if (v === "SI_CUMPLE") num += w;
    }
  }

  if (den <= 0) return null;
  return Math.round((num / den) * 100);
}

function TooltipContent({ criterioKey, payload }) {
  const { conf, just } = getDetail(payload, criterioKey);
  const confFmt = formatConfidence(conf);

  const b = payload?.quality?.breakdown?.[criterioKey];
  const weightStr = formatWeight(criterioKey);
  const earnedStr =
    b && typeof b.earned === "number" ? `${b.earned.toFixed(2)}%` : null;
  const applicableStr =
    b?.applicable === false ? "NO_APLICA" : b?.applicable === true ? "APLICA" : null;

  return (
    <EuiText size="xs">
      <strong>{LABELS[criterioKey] || criterioKey}</strong>

      {CRITICAL_KEYS.has(criterioKey) && (
        <>
          <br />
          <EuiBadge color="danger">Crítico</EuiBadge>
        </>
      )}

      {weightStr && (
        <>
          <br />
          <strong>Peso:</strong> {weightStr}
        </>
      )}

      {applicableStr && (
        <>
          <br />
          <strong>Aplicación:</strong> {applicableStr}
        </>
      )}

      {earnedStr && (
        <>
          <br />
          <strong>Aporta:</strong> {earnedStr}
        </>
      )}

      {confFmt && (
        <>
          <br />
          <strong>Confianza:</strong> {confFmt}
        </>
      )}

      {just && (
        <>
          <br />
          <strong>Justificación:</strong> {just}
        </>
      )}

      {!confFmt && !just && !weightStr && !earnedStr && (
        <>
          <br />
          Sin detalle guardado para este criterio.
        </>
      )}
    </EuiText>
  );
}

function formatConversationText(conversationTurns = []) {
  return (conversationTurns || [])
    .map((t) => `Speaker ${t?.speaker ?? "?"}: ${t?.text ?? ""}`.trim())
    .join("\n");
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadJson(filename, obj) {
  const text = JSON.stringify(obj, null, 2);
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Bauhaus008qOutbound({
  callId,
  hasRecording,
  companyName,
  iconSrc,
  token,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [payload, setPayload] = useState(null);
  const [convTab, setConvTab] = useState("chat");

  const normalizedCN = (companyName || localStorage.getItem("CN") || "")
    .toString()
    .trim()
    .toLowerCase();

  const canShow = normalizedCN === "bauhaus españa" && Boolean(hasRecording) && Boolean(callId);
  const authToken = token || sessionStorage.getItem("krakenT") || "";

  const fetchData = async () => {
    console.log("FETCH OUTBOUND", callId);
    setLoading(true);
    setLoadError("");
    setPayload(null);
    try {
      const res = await BauhausService.call_by_id_outbound({ token: authToken, callId });
      setPayload(res.data?.data || null);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "No se pudo obtener la información de la llamada";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onOpen = async () => {
    setOpen(true);
    await fetchData();
  };

  const onClose = () => {
    setOpen(false);
    setLoadError("");
    setPayload(null);
  };

  const normalizedPayload = useMemo(() => {
    if (!payload) return null;
    const etiqueta = payload?.ticket?.etiquetaLlamada ?? payload?.etiquetaLlamada ?? "Otros";
    return { ...payload, etiquetaLlamada: etiqueta };
  }, [payload]);

  const score100 = useMemo(() => {
    const q = normalizedPayload?.quality;
    if (q && typeof q.score100 === "number") return Math.round(q.score100);
    return computeScore100Fallback(normalizedPayload);
  }, [normalizedPayload]);

  const failedCriticalKeys = useMemo(() => {
    const fromQuality = normalizedPayload?.quality?.failedCriticalKeys;
    if (Array.isArray(fromQuality)) return fromQuality;

    return Array.from(CRITICAL_KEYS).filter((k) => {
      const v = String(getEvalValue(normalizedPayload, k) || "").toUpperCase();
      return v === "NO_CUMPLE";
    });
  }, [normalizedPayload]);

  const criticalFail = failedCriticalKeys.length > 0;

  const buildRows = (keys, data) =>
    keys.map((k) => {
      let value = getEvalValue(data, k);

      if (k === "etiquetaLlamada") value = data?.etiquetaLlamada;
      if (k === "agent") value = data?.agent;
      if (k === "agent_email") value = data?.agent_email;
      if (k === "queue") value = data?.queue;
      if (k === "extension") value = data?.extension;
      if (k === "src") value = data?.src;
      if (k === "dst") value = data?.dst;
      if (k === "direction") value = data?.direction;
      if (k === "answered_time") value = data?.answered_time;
      if (k === "dialed_time") value = data?.dialed_time;

      if (k === "valoracionAgente") {
        return {
          id: k,
          criterioKey: k,
          criterio: LABELS[k] || k,
          estado: value != null ? `${value}` : "-",
          dotColor: value != null ? "primary" : "subdued",
          tooltip: false,
        };
      }

      if (k === "sentimientoCliente") {
        const color =
          value === "positive" ? "success" : value === "negative" ? "danger" : "subdued";
        return {
          id: k,
          criterioKey: k,
          criterio: LABELS[k] || k,
          estado: value || "-",
          dotColor: color,
          tooltip: false,
        };
      }

      if (
        [
          "etiquetaLlamada",
          "agent",
          "agent_email",
          "queue",
          "extension",
          "src",
          "dst",
          "direction",
          "answered_time",
          "dialed_time",
        ].includes(k)
      ) {
        return {
          id: k,
          criterioKey: k,
          criterio: LABELS[k] || k,
          estado: value || "-",
          dotColor: value ? "primary" : "subdued",
          tooltip: false,
        };
      }

      return {
        id: k,
        criterioKey: k,
        criterio: LABELS[k] || k,
        estado: value ?? "NO_DETECTADO",
        dotColor: STATUS_COLOR(value),
        tooltip: true,
      };
    });

  const columns = [
    {
      field: "criterio",
      name: "Criterio",
      truncateText: true,
      width: "70%",
      render: (txt, item) => (
        <EuiFlexGroup gutterSize="s" alignItems="center">
          <EuiFlexItem grow={false}>
            {CRITICAL_KEYS.has(item.criterioKey) ? <EuiBadge color="danger">Crítico</EuiBadge> : null}
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">{txt}</EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      field: "estado",
      name: "Estado",
      align: "right",
      width: "30%",
      render: (estado, item) => {
        const cell = (
          <EuiFlexGroup gutterSize="s" alignItems="center" justifyContent="flexEnd">
            <EuiFlexItem grow={false}>
              <EuiHealth color={item.dotColor} />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="s">{estado}</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );

        if (item.tooltip) {
          return (
            <EuiToolTip
              position="top"
              display="block"
              content={<TooltipContent criterioKey={item.criterioKey} payload={normalizedPayload} />}
            >
              <span style={{ cursor: "help", display: "inline-block" }}>{cell}</span>
            </EuiToolTip>
          );
        }

        return cell;
      },
    },
  ];

  if (!canShow) return null;

  return (
    <>
      <EuiToolTip content="Ver evaluación de calidad (Bauhaus 008Q Outbound)">
        <EuiButtonIcon
          aria-label="Bauhaus 008Q Outbound"
          display="base"
          onClick={onOpen}
          iconType={iconSrc ? iconSrc : "inspect"}
        />
      </EuiToolTip>

      {open && (
        <EuiModal onClose={onClose} style={{ width: 920 }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <EuiFlexGroup gutterSize="s" alignItems="center">
                <EuiFlexItem grow={false}>
                  {iconSrc ? (
                    <img src={iconSrc} alt="008Q" style={{ width: 32, height: 32, display: "block" }} />
                  ) : (
                    <EuiIcon type="inspect" />
                  )}
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <span>Bauhaus 008Q — Evaluación de calidad outbound</span>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            {loading && (
              <EuiFlexGroup justifyContent="center" alignItems="center">
                <EuiLoadingSpinner size="xl" />
              </EuiFlexGroup>
            )}

            {!loading && loadError && (
              <EuiCallOut title="No se pudo cargar la información" color="danger" iconType="alert">
                <p>{loadError}</p>
              </EuiCallOut>
            )}

            {!loading && !loadError && normalizedPayload && (
              <>
                <EuiPanel paddingSize="m">
                  <EuiFlexGroup alignItems="center" gutterSize="m" wrap justifyContent="between">
                    <EuiFlexItem grow={false}>
                      <EuiStat
                        title={score100 == null ? "—" : `${score100}/100`}
                        description={LABELS.score100}
                        titleColor={
                          score100 == null
                            ? "subdued"
                            : score100 >= 85
                            ? "success"
                            : score100 >= 70
                            ? "warning"
                            : "danger"
                        }
                      />
                    </EuiFlexItem>

                    <EuiFlexItem>
                      <EuiText size="xs" color="subdued">
                        NO_APLICA no penaliza (se excluye del denominador).
                      </EuiText>

                      {criticalFail && (
                        <>
                          <EuiSpacer size="s" />
                          <EuiCallOut
                            title="Fallo en ítems críticos"
                            color="danger"
                            iconType="alert"
                            size="s"
                          >
                            {failedCriticalKeys?.length ? (
                              <EuiFlexGroup gutterSize="s" wrap>
                                {failedCriticalKeys.map((k) => (
                                  <EuiFlexItem key={k} grow={false}>
                                    <EuiBadge color="danger">{LABELS[k] || k}</EuiBadge>
                                  </EuiFlexItem>
                                ))}
                              </EuiFlexGroup>
                            ) : (
                              <p>Se marcó criticalFail pero no hay lista de críticos fallados.</p>
                            )}
                          </EuiCallOut>
                        </>
                      )}
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiPanel>

                <EuiSpacer size="m" />

                {(normalizedPayload.summarization ||
                  (normalizedPayload.topics && normalizedPayload.topics.length)) && (
                  <EuiPanel paddingSize="m">
                    <EuiTitle size="xs">
                      <h3>Resumen</h3>
                    </EuiTitle>
                    <EuiSpacer size="s" />
                    {normalizedPayload.summarization && (
                      <>
                        <EuiText size="s">{normalizedPayload.summarization}</EuiText>
                        <EuiSpacer size="m" />
                      </>
                    )}
                    {normalizedPayload.topics && normalizedPayload.topics.length > 0 && (
                      <>
                        <EuiText size="s">
                          <strong>{LABELS.topics}:</strong>
                        </EuiText>
                        <EuiSpacer size="xs" />
                        <EuiFlexGroup gutterSize="s" wrap>
                          {normalizedPayload.topics.map((t, idx) => (
                            <EuiFlexItem key={`${t}-${idx}`} grow={false}>
                              <EuiBadge color="hollow">{t}</EuiBadge>
                            </EuiFlexItem>
                          ))}
                        </EuiFlexGroup>
                      </>
                    )}
                  </EuiPanel>
                )}

                <EuiSpacer size="m" />
                <EuiSpacer size="m" />

                {Array.isArray(normalizedPayload?.conversationTurns) &&
                  normalizedPayload.conversationTurns.length > 0 &&
                  (() => {
                    const turns = normalizedPayload.conversationTurns;
                    const convText = formatConversationText(turns);

                    const baseName = `conversation_${callId || normalizedPayload.call_id || "call"}`;
                    const fileTxt = `${baseName}.txt`;
                    const fileJson = `${baseName}.json`;

                    return (
                      <EuiPanel paddingSize="m">
                        <EuiFlexGroup alignItems="center" justifyContent="spaceBetween" gutterSize="m">
                          <EuiFlexItem grow={false}>
                            <EuiTitle size="xs">
                              <h3>Conversación</h3>
                            </EuiTitle>
                          </EuiFlexItem>

                          <EuiFlexItem grow={false}>
                            <EuiFlexGroup gutterSize="s" alignItems="center">
                              <EuiFlexItem grow={false}>
                                <EuiCopy textToCopy={convText}>
                                  {(copy) => (
                                    <EuiButtonEmpty size="s" iconType="copyClipboard" onClick={copy}>
                                      Copiar
                                    </EuiButtonEmpty>
                                  )}
                                </EuiCopy>
                              </EuiFlexItem>

                              <EuiFlexItem grow={false}>
                                <EuiButton
                                  size="s"
                                  iconType="download"
                                  onClick={() => downloadText(fileTxt, convText)}
                                >
                                  Descargar TXT
                                </EuiButton>
                              </EuiFlexItem>

                              <EuiFlexItem grow={false}>
                                <EuiButtonEmpty
                                  size="s"
                                  iconType="download"
                                  onClick={() => downloadJson(fileJson, turns)}
                                >
                                  JSON
                                </EuiButtonEmpty>
                              </EuiFlexItem>
                            </EuiFlexGroup>
                          </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiSpacer size="s" />

                        <EuiTabs size="s">
                          <EuiTab isSelected={convTab === "chat"} onClick={() => setConvTab("chat")}>
                            Vista chat
                          </EuiTab>
                          <EuiTab isSelected={convTab === "text"} onClick={() => setConvTab("text")}>
                            Texto
                          </EuiTab>
                          <EuiTab isSelected={convTab === "json"} onClick={() => setConvTab("json")}>
                            JSON
                          </EuiTab>
                        </EuiTabs>

                        <EuiSpacer size="m" />

                        {convTab === "chat" && (
                          <EuiFlexGroup direction="column" gutterSize="s">
                            {turns.map((t, idx) => {
                              const speaker = t?.speaker ?? "?";
                              const isEven = typeof speaker === "number" ? speaker % 2 === 0 : false;
                              return (
                                <EuiFlexItem key={idx}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: isEven ? "flex-start" : "flex-end",
                                    }}
                                  >
                                    <div
                                      style={{
                                        maxWidth: "78%",
                                        padding: "10px 12px",
                                        borderRadius: 14,
                                        border: "1px solid #D3DAE6",
                                        background: isEven ? "#F5F7FA" : "#FFF",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                                      }}
                                    >
                                      <EuiText size="xs" color="subdued">
                                        <strong>Speaker {speaker}</strong>
                                      </EuiText>
                                      <EuiText size="s" style={{ marginTop: 4 }}>
                                        {t?.text || ""}
                                      </EuiText>
                                    </div>
                                  </div>
                                </EuiFlexItem>
                              );
                            })}
                          </EuiFlexGroup>
                        )}

                        {convTab === "text" && (
                          <EuiCodeBlock language="text" isCopyable overflowHeight={260} fontSize="s">
                            {convText}
                          </EuiCodeBlock>
                        )}

                        {convTab === "json" && (
                          <EuiCodeBlock language="json" isCopyable overflowHeight={260} fontSize="s">
                            {JSON.stringify(turns, null, 2)}
                          </EuiCodeBlock>
                        )}
                      </EuiPanel>
                    );
                  })()}

                {SECTIONS.map((section) => {
                  const rows = buildRows(section.keys, normalizedPayload);
                  const hasData = rows.some(
                    (r) => r.estado && r.estado !== "NO_DETECTADO" && r.estado !== "-"
                  );

                  return (
                    <EuiPanel key={section.title} paddingSize="m" style={{ marginBottom: 12 }}>
                      <EuiTitle size="xs">
                        <h3>
                          {section.title}
                          {section.percent ? ` · ${section.percent}` : ""}
                        </h3>
                      </EuiTitle>
                      <EuiSpacer size="s" />
                      <EuiBasicTable items={rows} columns={columns} tableCaption={section.title} compressed />
                      {!hasData && (
                        <>
                          <EuiSpacer size="s" />
                          <EuiText size="xs" color="subdued">
                            No se detectaron valores para esta sección.
                          </EuiText>
                        </>
                      )}
                    </EuiPanel>
                  );
                })}

                <EuiSpacer size="m" />
                <EuiHorizontalRule margin="m" />
                <EuiTitle size="xxs">
                  <h4>_source (JSON)</h4>
                </EuiTitle>
                <EuiSpacer size="s" />
                <EuiCodeBlock language="json" isCopyable overflowHeight={280} fontSize="s" lineNumbers>
                  {JSON.stringify(normalizedPayload, null, 2)}
                </EuiCodeBlock>
              </>
            )}
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButton onClick={onClose} fill>
              Cerrar
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
}